import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Sparkle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

const easeOut = [0.16, 1, 0.3, 1];

/**
 * Compact "Protection is active" strip for the dashboard.
 * Shows "X days left in trial" during trialing, or the standard active state.
 */
export default function ProtectionBanner() {
    const { user } = useAuth();
    const { t } = useI18n();
    const [state, setState] = useState(null);

    useEffect(() => {
        if (!user || user.plan !== "premium") return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/paddle/status");
                if (!cancelled) setState(data);
            } catch { /* passive */ }
        })();
        return () => { cancelled = true; };
    }, [user]);

    if (!user || user.plan !== "premium" || !state) return null;

    const inTrial = state.in_trial;
    const days = state.days_remaining_in_trial;
    const canceled = state.subscription_status === "canceled_pending";

    let label;
    if (inTrial && typeof days === "number") {
        label = t("protection.trial_days")
            .replace("{days}", String(days))
            .replace("{s}", days === 1 ? "" : t("protection.plural_s"));
    } else if (canceled) {
        label = t("protection.canceled_pending");
    } else {
        label = t("protection.active");
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: easeOut }}
            data-testid="protection-banner"
            className="relative mx-auto max-w-4xl mt-4 sm:mt-6 px-4"
        >
            <div className="rounded-2xl border border-emerald-500/20 bg-gradient-to-r from-emerald-500/8 via-white/[0.03] to-paw-terra/8 backdrop-blur px-4 py-3 sm:px-5 sm:py-3.5 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-emerald-500/15 ring-1 ring-emerald-500/25 flex items-center justify-center flex-shrink-0">
                    <ShieldCheck size={16} weight="fill" className="text-emerald-300" />
                </div>
                <div className="min-w-0 flex-1">
                    <p data-testid="protection-banner-label" className="text-[14px] sm:text-[15px] font-semibold text-white leading-tight flex items-center gap-2 flex-wrap">
                        {label}
                        {inTrial && (
                            <span data-testid="protection-banner-trial-chip" className="inline-flex items-center gap-1 rounded-full bg-paw-terra/15 text-paw-terra ring-1 ring-paw-terra/25 px-2 py-0.5 text-[11px] font-medium">
                                <Sparkle size={10} weight="fill" />
                                {t("protection.trial_chip")}
                            </span>
                        )}
                    </p>
                    <p className="mt-0.5 text-[12px] sm:text-[13px] text-white/55 leading-snug">
                        {t("protection.sub")}
                    </p>
                </div>
            </div>
        </motion.div>
    );
}
